/**
 * Presentation rules for a Google Ads conversion upload row.
 *
 * Pure, so the mapping from ledger status to what an operator actually reads is
 * unit-testable and can't drift between the badge and the explanatory text.
 */

export type GoogleAdsConversionStatus =
  | 'pending'
  | 'processing'
  | 'uploaded'
  | 'validated'
  | 'skipped_no_identifier'
  | 'skipped_disabled'
  | 'failed'

export type BadgeVariant = 'default' | 'secondary' | 'destructive' | 'outline'

export interface StatusPresentation {
  readonly label: string
  readonly variant: BadgeVariant
  /** One line explaining what this status actually means for the booking. */
  readonly meaning: string
}

export const CONVERSION_STATUS_PRESENTATION: Record<
  GoogleAdsConversionStatus,
  StatusPresentation
> = {
  uploaded: {
    label: 'Uploaded',
    variant: 'default',
    meaning: 'Google Ads accepted and recorded this conversion.',
  },
  validated: {
    label: 'Validated (dry run)',
    variant: 'secondary',
    // The distinction that matters most: a dry run looks like a success at the
    // API but records NOTHING in Google Ads.
    meaning:
      'Sent with the validate-only flag: Google accepted it but recorded nothing. ' +
      'No conversion exists in Google Ads.',
  },
  pending: {
    label: 'Queued for retry',
    variant: 'outline',
    meaning: 'A previous attempt failed temporarily. It retries automatically.',
  },
  processing: {
    label: 'Sending',
    variant: 'outline',
    meaning: 'An upload is in flight right now.',
  },
  failed: {
    label: 'Failed',
    variant: 'destructive',
    meaning: 'Google rejected it, or every retry was used up. Safe to retry manually.',
  },
  skipped_disabled: {
    label: 'Skipped',
    variant: 'secondary',
    meaning: 'Nothing was sent — see the reason below.',
  },
  skipped_no_identifier: {
    label: 'Skipped — no identifier',
    variant: 'secondary',
    meaning:
      'This booking had no click ID and no email or phone to match on, so Google ' +
      'had no way to attribute it.',
  },
}

const FALLBACK: StatusPresentation = {
  label: 'Unknown',
  variant: 'outline',
  meaning: 'Unrecognised status.',
}

export function presentConversionStatus(status: string | null | undefined): StatusPresentation {
  if (!status) return FALLBACK
  return CONVERSION_STATUS_PRESENTATION[status as GoogleAdsConversionStatus] ?? FALLBACK
}

/**
 * Whether a manual retry can do anything.
 *
 * 'uploaded' is already at Google and 'processing' is in flight; re-sending
 * either only muddies the ledger, since Google dedups on the transaction id.
 */
export function canRetryConversion(status: string | null | undefined): boolean {
  if (!status) return true
  return !['uploaded', 'processing'].includes(status)
}

/** Which identifier Google was given to match the conversion on. */
export function describeIdentifiers(row: {
  gclid: string | null
  had_email: boolean
  had_phone: boolean
  event_payload: unknown
}): string {
  const parts: string[] = []

  if (row.gclid) {
    parts.push('gclid')
  } else {
    // gbraid/wbraid have no column — they live in the frozen request payload.
    const adIds = extractAdIdentifiers(row.event_payload)
    if (adIds.gbraid) parts.push('gbraid')
    if (adIds.wbraid) parts.push('wbraid')
  }

  if (row.had_email) parts.push('hashed email')
  if (row.had_phone) parts.push('hashed phone')

  return parts.length > 0 ? parts.join(' + ') : 'none'
}

/** Pull the ad identifiers back out of the frozen IngestEventsRequest. */
export function extractAdIdentifiers(payload: unknown): {
  gclid?: string
  gbraid?: string
  wbraid?: string
} {
  try {
    const events = (payload as { events?: { adIdentifiers?: Record<string, string> }[] })?.events
    return events?.[0]?.adIdentifiers ?? {}
  } catch {
    return {}
  }
}
